/**
 * Admin — Tipi di abbonamento (`erp-subscription-types`).
 *
 * È il catalogo da cui HR sceglie quando assegna un abbonamento a una persona: nome, categoria,
 * costo di listino, periodicità, posti, preavviso di disdetta e chi ha diritto. **Un tipo con righe
 * assegnate non si cancella**: il BE risponde 409 e il gesto giusto è disattivarlo, così non entra
 * più nelle assegnazioni nuove ma le vecchie continuano a dire di che cosa erano.
 *
 * Da qui si fa anche il primo caricamento massivo da CSV: è il posto dove sta chi ha appena
 * compilato il catalogo e deve riversare gli abbonamenti già in essere.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  ActionIcon, Badge, Button, Group, NumberInput, Select, SimpleGrid, Switch, Table, Text, Textarea,
  TextInput, Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import {
  IconCreditCard, IconFileUpload, IconPencil, IconPlus, IconShieldCheck, IconTrash, IconListDetails, IconUsers,
} from '@tabler/icons-react';
import { useAbility } from '@/app/providers/AbilityProvider';
import { MaeCard, MaeField, MaeFormSection, MaeModal, MaePageHead, MaeShell } from '@/shared/desktop/MaeShell';
import { maeTableStyles } from '@/shared/desktop/MaeShell.utils';
import { StatCard } from '@/shared/components/StatCard';
import { MaeAsync } from '@/shared/components/MaeAsync';
import { Paginated } from '@/shared/components/DataTable/TablePagination';
import { notifyError, notifySuccess } from '@/shared/lib/notify';
import { formatMoney } from '@/shared/lib/format';
import {
  subscriptionErrorMessage, useCreateSubscriptionType, useDeleteSubscriptionType, useEligibilityRules, useSeats,
  useSubscriptionTypes, useUpdateSubscriptionType,
} from '@/features/subscriptions/api';
import {
  BILLING_PERIOD_OPTIONS, SUBSCRIPTION_CATEGORY_OPTIONS, billingPeriodLabel, categoryLabel, seatsLabel,
  type SubscriptionTypeRow,
} from '@/features/subscriptions/subscriptions';
import { EligibilityRulesDrawer } from '@/features/subscriptions/EligibilityRulesDrawer';
import { SubscriptionImportModal } from '@/features/subscriptions/SubscriptionImportModal';

export const Route = createFileRoute('/_app/admin/subscription-types')({ component: SubscriptionTypesPage });

interface TypeForm {
  name: string;
  category: string | null;
  description: string;
  defaultCost: number | string;
  currency: string;
  billingPeriod: string | null;
  noticeDays: number | string;
  seats: number | string;
  isActive: boolean;
  sortOrder: number | string;
}

const EMPTY: TypeForm = {
  name: '', category: null, description: '', defaultCost: '', currency: 'EUR', billingPeriod: 'monthly',
  noticeDays: '', seats: '', isActive: true, sortOrder: 0,
};

function SubscriptionTypesPage() {
  const ability = useAbility();
  const [editing, setEditing] = useState<SubscriptionTypeRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [rulesFor, setRulesFor] = useState<SubscriptionTypeRow | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const typesQ = useSubscriptionTypes();
  const seatsQ = useSeats();
  const rulesQ = useEligibilityRules();
  const create = useCreateSubscriptionType();
  const update = useUpdateSubscriptionType();
  const remove = useDeleteSubscriptionType();

  const types = useMemo(() => typesQ.data?.data ?? [], [typesQ.data]);
  const seatsById = useMemo(() => new Map((seatsQ.data ?? []).map((s) => [s.typeId, s])), [seatsQ.data]);
  const rulesCount = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rulesQ.data?.data ?? []) {
      const id = r.subscriptionType?.id;
      if (id) m.set(id, (m.get(id) ?? 0) + 1);
    }
    return m;
  }, [rulesQ.data]);

  const canWrite = ability.can('create', 'ErpSubscriptionType');
  const canUpdate = ability.can('update', 'ErpSubscriptionType');
  const canDelete = ability.can('delete', 'ErpSubscriptionType');
  const canRules = ability.can('create', 'ErpSubscriptionTypeEligibility');
  const canImport = ability.can('readAll', 'ErpEmployeeSubscription') || ability.can('manage', 'all');
  const activeCount = types.filter((t) => t.isActive !== false).length;
  const seatsUsed = (seatsQ.data ?? []).reduce((s, x) => s + x.used, 0);

  const form = useForm<TypeForm>({
    initialValues: EMPTY,
    validate: {
      name: (v) => (v.trim() ? null : 'Il nome è obbligatorio'),
      currency: (v) => (/^[A-Z]{3}$/.test(v) ? null : 'Codice ISO di 3 lettere (es. EUR)'),
      defaultCost: (v) => (v === '' || (typeof v === 'number' && v >= 0) ? null : 'Il costo non può essere negativo'),
      noticeDays: (v) => (v === '' || (typeof v === 'number' && v >= 0) ? null : 'I giorni non possono essere negativi'),
      seats: (v) => (v === '' || (typeof v === 'number' && v >= 1) ? null : 'Almeno un posto, oppure vuoto per nessun limite'),
    },
  });

  function openCreate() {
    setEditing(null);
    form.setValues(EMPTY);
    form.resetDirty(EMPTY);
    setModalOpen(true);
  }

  function openEdit(t: SubscriptionTypeRow) {
    setEditing(t);
    const values: TypeForm = {
      name: t.name ?? '',
      category: t.category ?? null,
      description: t.description ?? '',
      defaultCost: typeof t.defaultCost === 'number' ? t.defaultCost : '',
      currency: t.currency ?? 'EUR',
      billingPeriod: t.billingPeriod ?? null,
      noticeDays: typeof t.noticeDays === 'number' ? t.noticeDays : '',
      seats: typeof t.seats === 'number' ? t.seats : '',
      isActive: t.isActive !== false,
      sortOrder: typeof t.sortOrder === 'number' ? t.sortOrder : 0,
    };
    form.setValues(values);
    form.resetDirty(values);
    setModalOpen(true);
  }

  async function submit(values: TypeForm) {
    const attributes = {
      name: values.name.trim(),
      category: values.category,
      description: values.description.trim() || null,
      defaultCost: typeof values.defaultCost === 'number' ? values.defaultCost : null,
      currency: values.currency.toUpperCase(),
      billingPeriod: values.billingPeriod,
      noticeDays: typeof values.noticeDays === 'number' ? values.noticeDays : null,
      seats: typeof values.seats === 'number' ? values.seats : null,
      isActive: values.isActive,
      sortOrder: typeof values.sortOrder === 'number' ? values.sortOrder : 0,
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, attributes });
        notifySuccess('Tipo aggiornato');
      } else {
        await create.mutateAsync({ attributes });
        notifySuccess('Tipo creato');
      }
      setModalOpen(false);
    } catch (e) {
      notifyError(subscriptionErrorMessage(e));
    }
  }

  async function toggleActive(t: SubscriptionTypeRow, next: boolean) {
    try {
      await update.mutateAsync({ id: t.id, attributes: { isActive: next } });
      notifySuccess(next ? 'Tipo riattivato' : 'Tipo disattivato: non entra più nelle assegnazioni nuove');
    } catch (e) {
      notifyError(subscriptionErrorMessage(e));
    }
  }

  function confirmDelete(t: SubscriptionTypeRow) {
    modals.openConfirmModal({
      title: 'Elimina tipo di abbonamento',
      children: (
        <Text size="sm">
          Elimini <strong>{t.name}</strong>? Se qualcuno ha un abbonamento di questo tipo il sistema lo rifiuta:
          in quel caso disattivalo, così non compare più fra le scelte ma lo storico resta leggibile.
        </Text>
      ),
      labels: { confirm: 'Elimina', cancel: 'Annulla' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await remove.mutateAsync(t.id);
          notifySuccess('Tipo eliminato');
        } catch (e) {
          notifyError(subscriptionErrorMessage(e));
        }
      },
    });
  }

  return (
    <MaeShell>
      <MaePageHead
        title="Tipi di abbonamento"
        sub="Il catalogo da cui si assegnano gli abbonamenti alle persone: nome, categoria, listino, periodicità, posti e chi ha diritto"
        icon={<IconListDetails size={23} />}
        actions={(canWrite || canImport) ? (
          <Group gap={8}>
            {canImport && <Button variant="light" leftSection={<IconFileUpload size={16} />} onClick={() => setImportOpen(true)}>Importa da CSV</Button>}
            {canWrite && <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>Nuovo tipo</Button>}
          </Group>
        ) : undefined}
      />

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" mb="lg">
        <StatCard label="Tipi attivi" value={activeCount} icon={IconCreditCard} color="dark" />
        <StatCard label="Posti occupati" value={seatsUsed} icon={IconUsers} color="gray" />
        <StatCard label="Disattivati" value={types.length - activeCount} icon={IconListDetails} color={types.length - activeCount > 0 ? 'orange' : 'gray'} />
      </SimpleGrid>

      <MaeAsync
        query={typesQ}
        loading="Caricamento del catalogo…"
        empty={types.length === 0 && {
          icon: IconCreditCard,
          title: 'Nessun tipo di abbonamento',
          description: 'Finché il catalogo è vuoto non si può assegnare nessun abbonamento. Comincia dai più comuni: abbonamento trasporti, parcheggio, palestra, licenza software.',
          ...(canWrite ? { action: { label: 'Nuovo tipo', onClick: openCreate } } : {}),
        }}
      >
        <MaeCard padding={0}>
          <Paginated rows={types} pageSize={15} label="tipi" barPad={16}>
            {(page) => (
              <Table.ScrollContainer minWidth={980}>
                <Table striped highlightOnHover verticalSpacing="sm" styles={maeTableStyles}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Tipo</Table.Th>
                      <Table.Th>Categoria</Table.Th>
                      <Table.Th>Periodicità</Table.Th>
                      <Table.Th>Listino</Table.Th>
                      <Table.Th>Posti</Table.Th>
                      <Table.Th>Chi ha diritto</Table.Th>
                      <Table.Th>Attivo</Table.Th>
                      <Table.Th w={90} />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {page.map((t) => {
                      const seat = seatsById.get(t.id);
                      const full = t.seats != null && seat != null && (seat.available ?? 0) <= 0;
                      const nRules = rulesCount.get(t.id) ?? 0;
                      return (
                        <Table.Tr key={t.id} style={t.isActive === false ? { opacity: 0.55 } : undefined}>
                          <Table.Td>
                            <Text fw={700} size="sm">{t.name ?? '—'}</Text>
                            <Text size="xs" c="dimmed">
                              {[t.description, t.noticeDays ? `preavviso ${t.noticeDays} gg` : null].filter(Boolean).join(' · ')}
                            </Text>
                          </Table.Td>
                          <Table.Td><Badge size="sm" variant="light" color="blue">{categoryLabel(t.category)}</Badge></Table.Td>
                          <Table.Td><Text size="sm">{billingPeriodLabel(t.billingPeriod)}</Text></Table.Td>
                          <Table.Td>
                            <Text size="sm" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {formatMoney(t.defaultCost, t.currency ?? 'EUR')}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            {t.seats == null
                              ? <Text size="sm" c="dimmed">Illimitati</Text>
                              : (
                                <Tooltip label={full ? 'Tutti i posti sono occupati: le assegnazioni nuove vengono rifiutate' : 'Posti occupati su disponibili'}>
                                  <Badge size="sm" variant="light" color={full ? 'red' : 'teal'}>{seatsLabel(seat ?? { typeId: t.id, seats: t.seats, used: 0, available: t.seats })}</Badge>
                                </Tooltip>
                              )}
                          </Table.Td>
                          <Table.Td>
                            <Button size="compact-xs" variant={nRules > 0 ? 'light' : 'subtle'} color={nRules > 0 ? 'blue' : 'gray'}
                              leftSection={<IconShieldCheck size={13} />} onClick={() => setRulesFor(t)}>
                              {nRules === 0 ? 'Tutti' : `${nRules} ${nRules === 1 ? 'regola' : 'regole'}`}
                            </Button>
                          </Table.Td>
                          <Table.Td>
                            {canUpdate ? (
                              <Switch
                                size="sm"
                                checked={t.isActive !== false}
                                disabled={update.isPending}
                                onChange={(e) => toggleActive(t, e.currentTarget.checked)}
                                aria-label={`Attiva ${t.name ?? 'tipo'}`}
                              />
                            ) : (
                              <Badge size="sm" variant="light" color={t.isActive === false ? 'gray' : 'green'}>
                                {t.isActive === false ? 'Disattivato' : 'Attivo'}
                              </Badge>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Group gap={4} justify="flex-end" wrap="nowrap">
                              {canUpdate && (
                                <Tooltip label="Modifica">
                                  <ActionIcon variant="subtle" color="gray" onClick={() => openEdit(t)} aria-label={`Modifica ${t.name ?? 'tipo'}`}>
                                    <IconPencil size={16} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                              {canDelete && (
                                <Tooltip label="Elimina">
                                  <ActionIcon variant="subtle" color="red" onClick={() => confirmDelete(t)} aria-label={`Elimina ${t.name ?? 'tipo'}`}>
                                    <IconTrash size={16} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Paginated>
        </MaeCard>
      </MaeAsync>

      <MaeModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Modifica tipo' : 'Nuovo tipo di abbonamento'}
        icon={<IconCreditCard size={17} />}
        footer={
          <Button onClick={() => form.onSubmit(submit)()} loading={create.isPending || update.isPending}>
            {editing ? 'Salva' : 'Crea'}
          </Button>
        }
      >
        <MaeFormSection title="Identificazione" sub="Il nome è quello che HR vede nella tendina quando assegna: deve bastare da solo.">
          <MaeField label="Nome" required>
            <TextInput placeholder="es. Abbonamento bus urbano" {...form.getInputProps('name')} />
          </MaeField>
          <Group grow>
            <MaeField label="Categoria">
              <Select placeholder="Seleziona…" data={[...SUBSCRIPTION_CATEGORY_OPTIONS]} clearable {...form.getInputProps('category')} />
            </MaeField>
            <MaeField label="Periodicità">
              <Select placeholder="Seleziona…" data={[...BILLING_PERIOD_OPTIONS]} clearable {...form.getInputProps('billingPeriod')} />
            </MaeField>
          </Group>
          <MaeField label="Descrizione">
            <Textarea autosize minRows={2} placeholder="Cosa comprende, con quale fornitore…" {...form.getInputProps('description')} />
          </MaeField>
        </MaeFormSection>

        <MaeFormSection title="Listino" sub="Un default per chi assegna: il costo vero si scrive sulla singola riga e può essere diverso.">
          <Group grow>
            <MaeField label="Costo di listino per periodo">
              <NumberInput min={0} step={1} decimalScale={2} placeholder="es. 35,00"
                leftSection={<Text size="xs" c="dimmed" pl={4} style={{ fontVariantNumeric: 'tabular-nums' }}>€</Text>} {...form.getInputProps('defaultCost')} />
            </MaeField>
            <MaeField label="Valuta" required>
              <TextInput maxLength={3} {...form.getInputProps('currency')}
                onChange={(e) => form.setFieldValue('currency', e.currentTarget.value.toUpperCase())} />
            </MaeField>
          </Group>
        </MaeFormSection>

        <MaeFormSection title="Posti e disdetta" sub="Per le licenze a pacchetto e per i contratti con preavviso. Entrambi facoltativi.">
          <Group grow>
            <MaeField label="Posti disponibili" hint="Vuoto = illimitati. Un posto è occupato da un abbonamento attivo o sospeso; le richieste in attesa non contano.">
              <NumberInput min={1} step={1} placeholder="Illimitati" {...form.getInputProps('seats')} />
            </MaeField>
            <MaeField label="Preavviso di disdetta (giorni)" hint="Il giorno «scadenza meno preavviso» chi gestisce riceve «ultimo giorno per disdire». Non parte se il rinnovo è automatico.">
              <NumberInput min={0} step={1} placeholder="Nessuno" {...form.getInputProps('noticeDays')} />
            </MaeField>
          </Group>
        </MaeFormSection>

        <MaeFormSection title="Altro">
          <MaeField label="Ordine" hint="Posizione nella tendina: i numeri bassi vengono prima.">
            <NumberInput min={0} step={1} {...form.getInputProps('sortOrder')} />
          </MaeField>
          <Switch
            label="Tipo attivo"
            description="Disattivalo per toglierlo dalle assegnazioni nuove senza toccare quelle esistenti."
            checked={form.values.isActive}
            onChange={(e) => form.setFieldValue('isActive', e.currentTarget.checked)}
          />
          {editing && (
            <Text size="xs" c="dimmed">Chi ha diritto a questo tipo si decide dalla colonna «Chi ha diritto» della tabella.</Text>
          )}
        </MaeFormSection>
      </MaeModal>

      <EligibilityRulesDrawer type={rulesFor} onClose={() => setRulesFor(null)} canWrite={canRules} />
      <SubscriptionImportModal opened={importOpen} onClose={() => setImportOpen(false)} />
    </MaeShell>
  );
}
